import InfoIcon from '@mui/icons-material/Info';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { StatusKnockoutTile } from '../StatusKnockoutTile/StatusKnockoutTile';
import { StickyActionBar } from '../StickyActionBar/StickyActionBar';
import {
  SIGN_IN_REQUIRED_BODY,
  SIGN_IN_REQUIRED_BUTTON,
  SIGN_IN_REQUIRED_TITLE,
} from './signInRequiredCopy';

// No semantic size token is 40px (the scale runs 32px, 48px), so the status glyph keeps the
// literal RecoverInvitation.tsx already uses for the same tile on every other reason. It carries
// over that pattern unchanged and waits on a design decision or a shared token.
const STATUS_ICON_SIZE = 40;

export type SignInRequiredProps = {
  onSignIn?: () => void;
  attribution?: ReactNode;
  'data-testid'?: string;
};

/**
 * The recovery page's neutral screen for the invitation action's 409 (#571 Q9). It shows the
 * text and one way forward, the normal sign-in, and nothing else: no email field and no form,
 * because asking for the action again would only answer 409 again, and no word about the invite's
 * state, because a signed-out visitor must not learn it. The layout follows RecoverInvitation's
 * own reasons (same tile, spacing and sticky action bar), minus the form and the
 * replacement-link notice, which only apply when a link is sent.
 */
export function SignInRequired({
  onSignIn,
  attribution,
  'data-testid': dataTestId,
}: SignInRequiredProps) {
  const theme = useTheme();
  // The column's padding, and the same value negated by the action bar below.
  const columnPadding = theme.semantic.spacing['2lg'];

  return (
    <Stack
      data-testid={dataTestId}
      sx={{
        gap: theme.semantic.spacing['2lg'],
        padding: columnPadding,
        maxWidth: theme.semantic.size.sm,
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <StatusKnockoutTile
        icon={<InfoIcon sx={{ fontSize: STATUS_ICON_SIZE }} />}
        data-testid={dataTestId && `${dataTestId}-status-tile`}
      />
      <Stack sx={{ gap: theme.semantic.spacing.xs }}>
        <Typography variant="h4">{SIGN_IN_REQUIRED_TITLE}</Typography>
        <Typography variant="body1">{SIGN_IN_REQUIRED_BODY}</Typography>
      </Stack>
      {attribution}
      {/* contentInset and parentPaddingBottom: the same two overrides, for the same reason, as
          RecoverInvitation's own bar (see the comments there). This Stack carries the same 24px
          padding, so the bar must not stack its own inset on top. */}
      <StickyActionBar
        data-testid="recover-invitation-actionbar"
        contentInset={theme.semantic.spacing.none}
        parentPaddingBottom={columnPadding}
      >
        <Button
          // ADR-0071: the one action on this screen. The visible label is COPY-571 string 2c.
          data-action="sign-in"
          size="large"
          variant="contained"
          type="button"
          fullWidth
          onClick={onSignIn}
        >
          {SIGN_IN_REQUIRED_BUTTON}
        </Button>
      </StickyActionBar>
    </Stack>
  );
}
